import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Plus } from 'lucide-react'
import type { UsersListResponse } from '@common/types'
import { fetchUsers } from '@frontend/lib/api'
import { AppLayout } from '@frontend/components/layout/AppLayout'
import { Button } from '@frontend/components/ui/button'
import { Card, CardContent } from '@frontend/components/ui/card'
import { Spinner } from '@frontend/components/ui/spinner'
import { UsersTable, AddUserModal } from '@frontend/components/users'

export function UserManagement() {
  const [addUserOpen, setAddUserOpen] = useState(false)

  const { data, isLoading, error } = useQuery<UsersListResponse>({
    queryKey: ['users'],
    queryFn: fetchUsers,
  })

  const users = data?.users ?? []

  return (
    <AppLayout>
      <div className="space-y-6">
        {/* Page header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">User Management</h1>
            <p className="mt-1 text-muted-foreground">Manage user roles and permissions</p>
          </div>
          <Button onClick={() => setAddUserOpen(true)}>
            <Plus className="h-4 w-4 mr-1" />
            Add User
          </Button>
        </div>

        {/* Main content card */}
        <Card className="shadow-sm">
          <CardContent className="p-0">
            {isLoading ? (
              <div className="flex justify-center py-16">
                <Spinner size="lg" />
              </div>
            ) : error ? (
              <div className="py-16 text-center">
                <p className="text-destructive">Failed to load users.</p>
                <p className="mt-1 text-sm text-muted-foreground">Please try refreshing the page.</p>
              </div>
            ) : (
              <div className="p-4">
                <UsersTable users={users} />
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Add User Modal */}
      <AddUserModal open={addUserOpen} onOpenChange={setAddUserOpen} />
    </AppLayout>
  )
}
